/**
 * 统一缓存管理器
 * 支持 localStorage（Web 端）和 uni-app storage（小程序端）
 * 支持过期时间、前缀隔离、自动 JSON 序列化
 *
 * @typedef {Object} CacheDriver
 * @property {(key: string) => any} get
 * @property {(key: string, value: any) => void} set
 * @property {(key: string) => void} remove
 * @property {() => void} clear
 * @property {() => string[]} keys
 *
 * @typedef {Object} CacheManagerOptions
 * @property {string} [prefix] - 缓存 key 前缀，用于命名空间隔离
 * @property {'uni' | 'localStorage'} [driver] - 存储驱动，自动检测
 */

const isUniApp = typeof uni !== 'undefined' && uni.getStorageSync;

function getLocalStorage() {
  return globalThis.localStorage ?? null;
}

function getDriver(driver) {
  if (driver === 'uni') {
    return {
      get: key => {
        try {
          return uni.getStorageSync(key);
        } catch {
          return null;
        }
      },
      set: (key, value) => uni.setStorageSync(key, value),
      remove: key => uni.removeStorageSync(key),
      clear: () => uni.clearStorageSync(),
      keys: () => {
        try {
          return uni.getStorageInfoSync()?.keys || [];
        } catch {
          return [];
        }
      },
    };
  }

  // 默认 localStorage
  return {
    get: key => {
      const storage = getLocalStorage();
      if (!storage) return null;
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      const storage = getLocalStorage();
      if (!storage) return;
      storage.setItem(key, value);
    },
    remove: key => {
      const storage = getLocalStorage();
      if (!storage) return;
      storage.removeItem(key);
    },
    clear: () => {
      const storage = getLocalStorage();
      if (!storage) return;
      storage.clear();
    },
    keys: () => {
      const storage = getLocalStorage();
      if (!storage) return [];
      try {
        return Object.keys(storage);
      } catch {
        return [];
      }
    },
  };
}

/**
 * 创建缓存管理器实例
 * @param {CacheManagerOptions} [options]
 * @returns {{
 *   get: (key: string, defaultValue?: any) => any,
 *   set: (key: string, value: any, options?: { expires?: number }) => void,
 *   remove: (key: string) => void,
 *   clear: () => void,
 *   has: (key: string) => boolean,
 *   keys: () => string[],
 *   size: () => number,
 * }}
 */
export function createCacheManager(options = {}) {
  const prefix = options.prefix ? `${options.prefix}:` : '';
  const driver = getDriver(options.driver || (isUniApp ? 'uni' : 'localStorage'));

  function wrapKey(key) {
    return prefix + key;
  }

  function unwrapKey(key) {
    return key.startsWith(prefix) ? key.slice(prefix.length) : key;
  }

  return {
    /**
     * 读取缓存
     * @param {string} key - 缓存 key
     * @param {any} [defaultValue=null] - 过期或不存在时的默认值
     * @returns {any}
     */
    get(key, defaultValue = null) {
      const raw = driver.get(wrapKey(key));
      if (raw === null || raw === undefined || raw === '') {
        return defaultValue;
      }

      try {
        const wrapped = JSON.parse(raw);
        // 检查是否带过期时间包装
        if (wrapped && typeof wrapped === 'object' && '__expires' in wrapped) {
          if (wrapped.__expires && Date.now() > wrapped.__expires) {
            this.remove(key);
            return defaultValue;
          }
          return wrapped.__value ?? defaultValue;
        }
        return wrapped ?? defaultValue;
      } catch {
        return raw ?? defaultValue;
      }
    },

    /**
     * 写入缓存
     * @param {string} key - 缓存 key
     * @param {any} value - 要存储的值
     * @param {{ expires?: number }} [options] - 可选配置
     * @param {number} [options.expires] - 过期时间（秒）
     */
    set(key, value, options = {}) {
      let data = value;
      if (options.expires && typeof options.expires === 'number') {
        data = {
          __value: value,
          __expires: Date.now() + options.expires * 1000,
        };
      }
      driver.set(wrapKey(key), JSON.stringify(data));
    },

    /**
     * 移除指定 key
     * @param {string} key
     */
    remove(key) {
      driver.remove(wrapKey(key));
    },

    /**
     * 清空当前前缀下的所有缓存
     */
    clear() {
      const allKeys = driver.keys();
      for (const k of allKeys) {
        if (k.startsWith(prefix)) {
          driver.remove(k);
        }
      }
    },

    /**
     * 判断是否包含指定 key
     */
    has(key) {
      return this.get(key) !== null;
    },

    /**
     * 获取当前前缀下的所有 key（不含前缀）
     */
    keys() {
      const allKeys = driver.keys();
      return allKeys.filter(k => k.startsWith(prefix)).map(unwrapKey);
    },

    /**
     * 获取当前前缀下的所有缓存条目数
     */
    size() {
      return this.keys().length;
    },
  };
}

/**
 * 默认缓存实例（无前缀，使用 localStorage / uni）
 */
export const cache = createCacheManager();
